import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { TranslateService } from '@ngx-translate/core';

import { ApplicationFacade, BankcardTicketsFacade, BankcardTransaction, NotificationFacade } from '@ridango/components';
import { valueMatchValidator, BaseComponent } from '@ridango/core';
import Env from '@ridango/core/env';
import { TransactionMessage } from '@ridango/core/lib/models/ecomm-form.interface';
import { EcommFormService } from '@ridango/core/lib/services';

import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'gl-customer-support-page',
    templateUrl: './customer-support-page.component.html',
    styleUrls: ['./customer-support-page.component.scss']
})
export class CustomerSupportPageComponent extends BaseComponent implements OnInit, OnDestroy {
    @ViewChild('iframe', { static: false }) iframe: ElementRef;

    customerForm: FormGroup;
    client_email: string;
    client_email_again: string;
    client_name: string;
    client_problem_description: string;
    client_phone: number;
    transactionId: number;
    matchEmail = false;
    bankcardSent = false;

    displayIframe = false;
    tpsEndpoint: string;
    hostApp: string;
    loading$ = this.bankcardTicketsFacade.loading$;
    tickets$ = this.bankcardTicketsFacade.tickets$;
    transaction$ = this.bankcardTicketsFacade.transaction$;
    success$ = this.bankcardTicketsFacade.feedbackSuccess$;

    private transaction: BankcardTransaction;
    private isDarkMode = false;

    @Output() login = new EventEmitter<any>();

    constructor(
        @Inject('ENV') public env: Env,
        private bankcardTicketsFacade: BankcardTicketsFacade,
        private translate: TranslateService,
        private notification: NotificationFacade,
        private applicationFacade: ApplicationFacade,
        private ecommFormService: EcommFormService
    ) {
        super();
        this.tpsEndpoint = this.env.TPS_BANKCARD_IFRAME_URL;
        this.hostApp = this.env.CEMV_HOST_APP;
    }

    ngOnInit() {
        this.ecommFormService
            .watchThemeChanges(
                this.applicationFacade.highContrast$,
                this.componentDestroyed$,
                () => this.iframe,
                () => this.displayIframe
            )
            .subscribe((isDarkMode) => {
                this.isDarkMode = isDarkMode;
            });

        this.bankcardTicketsFacade.fetchTransaction(this.env.REGION_ID);
        this.bankcardTicketsFacade.transaction$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((transaction: BankcardTransaction) => {
                if (transaction) {
                    this.transaction = transaction;
                    this.transaction.hostApp = this.hostApp;
                    this.transaction.lang = this.translate.currentLang;
                    this.displayIframe = true;
                }
            });

        this.bankcardTicketsFacade.error$.pipe(takeUntil(this.componentDestroyed$)).subscribe((error) => {
            if (error) {
                if (error.error.description) {
                    this.notification.error(this.translate.instant(error.error.description));
                } else {
                    this.notification.error(this.translate.instant(error.error));
                }
            }
        });

        this.customerForm = new FormGroup({
            client_name: new FormControl('', [Validators.required]),
            client_phone: new FormControl('', [Validators.required]),
            client_email: new FormControl('', [Validators.required, Validators.email]),
            client_email_again: new FormControl('', [
                Validators.required,
                Validators.email,
                valueMatchValidator('client_email')
            ]),
            client_problem_description: new FormControl('', [Validators.required]),
            transaction_id: new FormControl('', [Validators.required])
        });
    }

    ngOnDestroy() {
        this.bankcardTicketsFacade.cleanState();
        this.notification.removeAll();
        super.ngOnDestroy();
    }

    @HostListener('window:message', ['$event'])
    iframeMessage(event) {
        if (event.data) {
            if ('ready' in event.data && event.data.ready) {
                this.sendTransactionInfoToTPS();
            } else if (event.data.responseCodeName && event.data.responseCodeName === 'APPROVED') {
                this.notification.removeAll();
                this.bankcardSent = true;
                this.displayIframe = false;
            }
        }
    }

    sendTransactionInfoToTPS() {
        this.iframe.nativeElement.contentWindow.postMessage(this.getTransactionMessage(), this.tpsEndpoint);
    }

    private getTransactionMessage(): TransactionMessage {
        return this.ecommFormService.getTransactionMessage(this.transaction, this.isDarkMode, {
            useShortCardTokenForm: true,
            use3DSCardTokenForm: false,
            permissiveSubmitButton: true,
            placeholders: true
        });
    }

    submitDataCustomer() {
        this.customerForm.controls.transaction_id.setValue(this.transaction.transactionId);
        this.bankcardTicketsFacade.sendFeedback(this.customerForm.value);

        if (this.customerForm.valid) {
            this.login.emit(this.customerForm.value);
        } else {
            this.customerForm.markAsTouched();
        }
    }

    newSearch() {
        this.bankcardTicketsFacade.cleanState();
        this.notification.removeAll();
        this.bankcardTicketsFacade.fetchTransaction(this.env.REGION_ID);
        this.customerForm.reset();
        this.bankcardSent = false;
    }

    validateEmail() {
        this.customerForm.controls['client_email_again'].updateValueAndValidity();
    }
}
